import styled from '@emotion/styled';
import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import './SkillDialog.css';
import Skeleton from 'react-loading-skeleton';
import Button from '../../../../../components/button';
import ServerError from '../../../../../components/ServerError';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { SkillLevelSelect } from '../common/SkillLevelSelect';
import { SkillSingleSelect } from '../common/SkillSingleSelect';
import { SkillLevel } from '../common/types';
import { Skill } from '../common/types';
import { Col, Row } from 'react-bootstrap';
import { CancelButton } from '../../../../../components/button/CancelButton';
import { DataWithLoadingState } from '../../../../../utils/dataWithLoadingState';
import { canSelfRate } from '../common/skillSelfServiceMode';
import { MonthsExperienceInput } from '../common/MonthsExperienceInput';

export type Props = {
  show: boolean;
  inProgress: boolean;
  isSelfView: boolean;
  onClose: () => void;
  onSkillAdded: (
    skillId: string,
    level: SkillLevel,
    monthsExperience: number | null,
  ) => void;
  availableSkills: DataWithLoadingState<Skill>;
  translationNamespace?: string;
};

export const AddEmployeeSkillDialog: React.FC<Props> = ({
  show,
  onClose,
  availableSkills,
  inProgress,
  isSelfView,
  onSkillAdded,
  translationNamespace = 'skills.addEmployeeSkill',
}) => {
  const t = useNamespacedTranslation(translationNamespace);

  const [selectedSkillId, setSelectedSkillId] = useState<string | null>(null);
  const [selectedSkillLevel, setSelectedSkillLevel] =
    useState<SkillLevel>(null);
  const [selectedMonthsExperience, setSelectedMonthsExperience] = useState<
    number | null
  >(null);

  const handleSubmit = useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();
      if (selectedSkillId) {
        onSkillAdded(
          selectedSkillId,
          selectedSkillLevel,
          selectedMonthsExperience,
        );
      }
    },
    [
      onSkillAdded,
      selectedSkillId,
      selectedSkillLevel,
      selectedMonthsExperience,
    ],
  );

  const selectedSkill: Skill | undefined = Array.isArray(availableSkills)
    ? availableSkills.find((x) => x.id === selectedSkillId)
    : undefined;

  const isRateLockedForViewer =
    isSelfView &&
    !!selectedSkill &&
    !canSelfRate(selectedSkill.selfServiceMode);

  const isSkillLevelVisible =
    selectedSkill && selectedSkill.isRateable && !isRateLockedForViewer;

  useEffect(() => {
    if (isRateLockedForViewer && selectedSkillLevel !== null) {
      setSelectedSkillLevel(null);
    }
    if (isRateLockedForViewer && selectedMonthsExperience !== null) {
      setSelectedMonthsExperience(null);
    }
  }, [isRateLockedForViewer, selectedSkillLevel, selectedMonthsExperience]);

  const isLoading = availableSkills === 'loading';

  const renderContent = () => {
    if (availableSkills === 'error') {
      return <ServerError />;
    }
    return (
      <form className="d-flex flex-column" onSubmit={handleSubmit}>
        <div>{t('selectSkillText')}</div>
        <Row className="mt-3">
          <Col sm={isSkillLevelVisible ? 6 : 12} md={true}>
            <Label>{t('skill')}</Label>
            <div>
              {!isLoading ? (
                <SkillSingleSelect
                  allSkills={availableSkills}
                  value={selectedSkillId}
                  autoFocus={true}
                  onChange={setSelectedSkillId}
                />
              ) : (
                <Skeleton width={'100%'} height={40} />
              )}
            </div>
          </Col>
          {isSkillLevelVisible && (
            <Col sm={6} md={true}>
              <Label>{t('level')}</Label>
              <NegativeMargin>
                <SkillLevelSelect
                  value={selectedSkillLevel}
                  onChange={setSelectedSkillLevel}
                />
              </NegativeMargin>
            </Col>
          )}
        </Row>
        {selectedSkill && !isRateLockedForViewer && (
          <Row className="mt-3">
            <Col>
              <Label>{t('experience')}</Label>
              <MonthsExperienceInput
                value={selectedMonthsExperience}
                onChange={setSelectedMonthsExperience}
              />
            </Col>
          </Row>
        )}
        <div className="d-flex justify-content-end">
          <CancelButton
            className="mt-4 me-3"
            disabled={isLoading || inProgress}
            onClick={onClose}
          />
          <Button
            className="mt-4"
            disabled={isLoading || selectedSkillId === null}
            label={t('save')}
            type="submit"
            isProcessing={inProgress}
          />
        </div>
      </form>
    );
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      dialogClassName="skill-dialog-elevated">
      <Modal.Header closeButton className="card-header">
        <h4 className="card-header-title">{t('title')}</h4>
      </Modal.Header>
      <Modal.Body>{renderContent()}</Modal.Body>
    </Modal>
  );
};

const Label = styled.div({
  fontWeight: 600,
  marginBottom: '0.25rem',
});

const NegativeMargin = styled(Label)({
  marginLeft: '-10px',
});
